import React from 'react';

// Sticky right-rail guidance + a live "what this produces" figure for the
// pricing tabs. Read-only; recomputes from the same inputs the form edits.

export interface ConfigGuideProps {
  activeTab: string;
  hardware: { unitCost: number; utilization: number; profitMargin: number; rentalRate: number };
  application: { developmentCost: number; payBackPeriod: number; unitsPerYear: number; profitMargin: number };
  costs: { internetFee: number; awsCompute: number; awsStorage: number };
}

const HELP: Record<string, string> = {
  hardware: 'Turns the welding hardware unit cost into a monthly rental price, covering margin and idle-time (utilization).',
  application: 'Recovers software development cost across the expected units to set the per-unit monthly software fee.',
  licensing: 'Discounts applied to longer licence durations. Enter each band as a percentage.',
  costs: 'Recurring connectivity, cloud, spare, service and mobilization rates used across every proposal.',
};
const MODULE_HELP = 'Rate card for this module. These values are the baseline every new proposal inherits; a proposal can override them per quote.';

const fmt = (n: number) => `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

function livePreview(props: ConfigGuideProps): { label: string; value: string; formula: string } | null {
  const { activeTab, hardware, application, costs } = props;
  if (activeTab === 'hardware') {
    const m = hardware.profitMargin / 100, u = hardware.utilization / 100, r = hardware.rentalRate / 100;
    const sale = m < 1 ? hardware.unitCost / (1 - m) : 0;
    const billable = m < 1 && u > 0 ? sale / u : 0;
    return { label: 'Monthly rental / unit', value: fmt(billable * r), formula: 'cost ÷ (1 − margin) ÷ utilization × rental rate' };
  }
  if (activeTab === 'application') {
    const m = application.profitMargin / 100;
    const monthly = application.payBackPeriod > 0 ? application.developmentCost / application.payBackPeriod : 0;
    const perUnit = application.unitsPerYear > 0 ? monthly / application.unitsPerYear : 0;
    return { label: 'Software fee / unit / mo', value: fmt(m < 1 ? perUnit / (1 - m) : 0), formula: 'dev cost ÷ payback ÷ units ÷ (1 − margin)' };
  }
  if (activeTab === 'costs') {
    return { label: 'Fixed monthly / machine', value: fmt((costs.internetFee || 0) + (costs.awsCompute || 0) + (costs.awsStorage || 0)), formula: 'connectivity + cloud compute + cloud storage' };
  }
  return null;
}

export const ConfigGuide: React.FC<ConfigGuideProps> = (props) => {
  const help = HELP[props.activeTab] ?? MODULE_HELP;
  const preview = livePreview(props);
  return (
    <aside className="xl:sticky xl:top-6 flex flex-col gap-4">
      <div className="bg-white rounded-xl border border-[#E2E5EA] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5">
        <div className="text-[10px] font-semibold text-heritage-grey uppercase tracking-[0.06em] mb-2">About this tab</div>
        <p className="text-[13px] text-text-grey leading-relaxed">{help}</p>
      </div>

      {preview && (
        <div className="bg-white rounded-xl border border-[#E2E5EA] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5">
          <div className="text-[10px] font-semibold text-heritage-grey uppercase tracking-[0.06em] mb-2">Live preview</div>
          <div className="text-[11px] text-heritage-grey">{preview.label}</div>
          <div className="font-heading font-bold text-[26px] text-blueprint-blue leading-tight mt-0.5">{preview.value}</div>
          <div className="text-[11px] text-heritage-grey mt-2 leading-relaxed">{preview.formula}</div>
        </div>
      )}

      <div className="rounded-xl border border-[rgba(0,39,63,0.12)] bg-[rgba(0,39,63,0.04)] p-4">
        <p className="text-[12px] text-blueprint-blue leading-relaxed">
          These are <span className="font-semibold">project-wide defaults</span>. They apply to every new proposal and take effect the next time a proposal is calculated.
        </p>
      </div>
    </aside>
  );
};
